import React, { useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { Box, Container, Stack } from "@mui/material";
import { colors } from "../../../../theme";
import { Path } from "../../../../static/Constants";
import DashboardSidebar, { SIDEBAR_WIDTH } from "./DashboardSidebar";
import DashboardTopNav from "./DashboardTopNav";
import DashboardHeader from "./DashboardHeader";
import { CommonPageHeader, AccessDeniedScreen } from "../../ui";

/**
 * DashboardLayout - Unified framing component for Admin and Creator dashboard views.
 * Encapsulates responsive drawer state, desktop sidebar collapse, sticky top navigation,
 * page header slots, access control guards, and content container.
 */
const DashboardLayout = ({
  role,
  userName,
  userRole,
  creatorAddress = "",
  unreadCount = 0,
  title,
  subtitle,
  breadcrumbs,
  headerType = "common", // "common" | "dashboard" | "none"
  headerActions,
  actions,
  headerBadge,
  dateRangeText,
  onDateRangeChange,
  onFilterClick,
  headerSlot,
  headerSx = {},
  maxWidth = "xl",
  disableContainer = false,
  containerSx = {},
  contentSx = {},
  children,
  authChecked = false,
  isAuthorized = false,
  accessDeniedProps = {},
  sidebarOpen: controlledSidebarOpen,
  onToggleSidebar: controlledOnToggleSidebar,
  disableToast = true,
  sx = {},
}) => {
  const location = useLocation();

  // Determine effective role
  const isAdminRole =
    role === "admin" ||
    userRole?.toLowerCase().includes("admin") ||
    location.pathname.startsWith("/admin") ||
    location.pathname === Path.admin;

  const effectiveRole = role || (isAdminRole ? "admin" : "creator");
  const defaultUserName = isAdminRole ? "AutoBET Admin" : "Creator";
  const defaultUserRole = isAdminRole ? "Administrator" : "Creator";

  // Sidebar & Drawer State
  const [uncontrolledSidebarOpen, setUncontrolledSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isSidebarOpen =
    controlledSidebarOpen !== undefined
      ? controlledSidebarOpen
      : uncontrolledSidebarOpen;

  const handleToggleSidebar = () => {
    if (controlledOnToggleSidebar) {
      controlledOnToggleSidebar();
    } else {
      setUncontrolledSidebarOpen((prev) => !prev);
    }
  };

  const handleMobileToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const resolvedActions = headerActions || actions;

  // Render Header according to headerType or custom headerSlot
  const renderHeader = () => {
    if (headerSlot) {
      return headerSlot;
    }

    if (headerType === "none" || (!title && !subtitle && !breadcrumbs)) {
      return null;
    }

    if (headerType === "dashboard") {
      return (
        <DashboardHeader
          title={title}
          subtitle={subtitle}
          breadcrumbs={breadcrumbs}
          dateRangeText={dateRangeText}
          onDateRangeChange={onDateRangeChange}
          onFilterClick={onFilterClick}
          actionButton={resolvedActions}
          sx={headerSx}
        />
      );
    }

    return (
      <CommonPageHeader
        title={title}
        subtitle={subtitle}
        breadcrumbs={breadcrumbs}
        actions={resolvedActions}
        badge={headerBadge}
        sx={headerSx}
      />
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: colors.dashboard.canvas,
        ...sx,
      }}
    >
      {/* Sidebar Navigation */}
      <DashboardSidebar
        role={effectiveRole}
        open={isSidebarOpen}
        onToggle={handleToggleSidebar}
        mobileOpen={mobileOpen}
        onMobileToggle={handleMobileToggle}
      />

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: {
            md: isSidebarOpen ? `calc(100% - ${SIDEBAR_WIDTH}px)` : "100%",
          },
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
          transition: "width 0.25s ease-in-out",
          ...contentSx,
        }}
      >
        {/* Sticky Top Navbar */}
        <DashboardTopNav
          role={effectiveRole}
          onMobileToggle={handleMobileToggle}
          onToggleSidebar={handleToggleSidebar}
          sidebarOpen={isSidebarOpen}
          creatorAddress={creatorAddress}
          userName={userName || defaultUserName}
          userRole={userRole || defaultUserRole}
          unreadCount={unreadCount}
        />

        {/* Access Denied Guard if unauthorized */}
        {authChecked && !isAuthorized ? (
          <AccessDeniedScreen
            title={
              accessDeniedProps.title ||
              (isAdminRole ? "Access Denied" : "Account Access Denied")
            }
            message={
              accessDeniedProps.message ||
              (isAdminRole
                ? "You must be an administrator to view or manage this console."
                : `You must be a registered Creator or organization to view ${
                    typeof title === "string" && title ? title : "this page"
                  }.`)
            }
            actionText={
              accessDeniedProps.actionText ||
              (isAdminRole ? "Return to Home" : "Register as Organisation")
            }
            actionPath={
              accessDeniedProps.actionPath ||
              (isAdminRole ? Path.root : Path.registerasorg)
            }
            walletAddress={creatorAddress}
            {...accessDeniedProps}
          />
        ) : disableContainer ? (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            {renderHeader()}
            {children}
          </Box>
        ) : (
          <Container
            maxWidth={maxWidth}
            sx={{
              py: { xs: 2.5, md: 3.5 },
              px: { xs: 2, sm: 3, md: 4 },
              flex: 1,
              display: "flex",
              flexDirection: "column",
              ...containerSx,
            }}
          >
            <Stack spacing={3.5} sx={{ flex: 1 }}>
              {renderHeader()}
              {children}
            </Stack>
          </Container>
        )}
      </Box>
    </Box>
  );
};

DashboardLayout.propTypes = {
  role: PropTypes.oneOf(["admin", "creator", "organisation"]),
  userName: PropTypes.string,
  userRole: PropTypes.string,
  creatorAddress: PropTypes.string,
  unreadCount: PropTypes.number,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  breadcrumbs: PropTypes.oneOfType([PropTypes.array, PropTypes.node]),
  headerType: PropTypes.oneOf(["common", "dashboard", "none"]),
  headerActions: PropTypes.node,
  actions: PropTypes.node,
  headerBadge: PropTypes.node,
  dateRangeText: PropTypes.string,
  onDateRangeChange: PropTypes.func,
  onFilterClick: PropTypes.func,
  headerSlot: PropTypes.node,
  headerSx: PropTypes.object,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  disableContainer: PropTypes.bool,
  containerSx: PropTypes.object,
  contentSx: PropTypes.object,
  children: PropTypes.node,
  authChecked: PropTypes.bool,
  isAuthorized: PropTypes.bool,
  accessDeniedProps: PropTypes.object,
  sidebarOpen: PropTypes.bool,
  onToggleSidebar: PropTypes.func,
  disableToast: PropTypes.bool,
  sx: PropTypes.object,
};

export default DashboardLayout;
